import { calculateAgeForTest, displayPatientName, formatLongDate } from "../../../utils/CommonUtil";
import { useEffect, useState } from "react";

import { VFAFlatMetrics, VFATestResult } from "types/tests/specifics";
import { flattenComputedMetrics } from "@utils";
import { useSearchParams } from "react-router-dom";
import { colorVisionPlateThumbImages } from "@utils/test-specific-utils/ColorVisionImageUtils";
import useTestList from "../../../hooks/useTestList";

export interface PatientData {
  name: string;
  age: number | null;
  testDate: string;
}

export interface VisionTestDataParams {
  vfUuid1?: string;
  vfUuid2?: string;
  contrastUuid?: string;
  colorVisionUuid?: string;
}

function extractFirstTest(list: any): any | null {
  const tests = Array.isArray(list) ? list : list?.data;
  return Array.isArray(tests) && tests.length > 0 ? tests[0] : null;
}

export function useVisionTestData(params?: VisionTestDataParams | null) {
  const [searchParams] = useSearchParams();
  const vfUuid1 = params?.vfUuid1 ?? searchParams.get("vfUuid1");
  const vfUuid2 = params?.vfUuid2 ?? searchParams.get("vfUuid2");
  const contrastUuid = params?.contrastUuid ?? searchParams.get("contrastUuid");
  const colorVisionUuid = params?.colorVisionUuid ?? searchParams.get("colorVisionUuid");

  const { testList: vfTestList1, testListLoading: vfLoading1 } = useTestList(
    vfUuid1 ? { uuid: vfUuid1 } : {},
  );
  const { testList: vfTestList2, testListLoading: vfLoading2 } = useTestList(
    vfUuid2 ? { uuid: vfUuid2 } : {},
  );
  const { testList: contrastTestList, testListLoading: contrastLoading } = useTestList(
    contrastUuid ? { uuid: contrastUuid } : {},
  );
  const { testList: colorVisionTestList, testListLoading: colorVisionLoading } = useTestList(
    colorVisionUuid ? { uuid: colorVisionUuid } : {},
  );

  const [patientData, setPatientData] = useState<PatientData>({ name: "", age: 0, testDate: "" });
  const [vfTest1, setVfTest1] = useState<any>(null);
  const [vfMapValues1, setVfMapValues1] = useState<VFAFlatMetrics | null>(null);
  const [vfTest2, setVfTest2] = useState<any>(null);
  const [vfMapValues2, setVfMapValues2] = useState<VFAFlatMetrics | null>(null);
  const [contrastTest, setContrastTest] = useState<any>(null);
  const [colorVisionTest, setColorVisionTest] = useState<any>(null);
  function processVfTest(
    test: any,
    setTest: (t: any) => void,
    setMapValues: (v: VFAFlatMetrics | null) => void,
  ) {
    setTest(test);
    setPatientData({
      name: test.patient?.name ? displayPatientName(test.patient.name) : "Unknown",
      age: test.patient?.dob
        ? calculateAgeForTest(new Date(test.startDate), new Date(test.patient.dob))
        : 0,
      testDate: formatLongDate(test.startDate),
    });
    setMapValues(flattenComputedMetrics((test as VFATestResult).computedMetrics));
  }

  useEffect(() => {
    const test = extractFirstTest(vfTestList1);
    if (test) processVfTest(test, setVfTest1, setVfMapValues1);
  }, [vfTestList1]);

  useEffect(() => {
    const test = extractFirstTest(vfTestList2);
    if (test) processVfTest(test, setVfTest2, setVfMapValues2);
  }, [vfTestList2]);

  useEffect(() => {
    const test = extractFirstTest(contrastTestList);
    if (test) setContrastTest(test);
  }, [contrastTestList]);

  useEffect(() => {
    const test = extractFirstTest(colorVisionTestList);
    if (test) setColorVisionTest(test);
  }, [colorVisionTestList]);

  const loading = vfLoading1 || vfLoading2 || contrastLoading || colorVisionLoading;

  return {
    patientData,
    vfTest1,
    vfMapValues1,
    vfTest2,
    vfMapValues2,
    contrastTest,
    colorVisionTest,
    colorVisionImages: colorVisionPlateThumbImages,
    hasVf1: !!vfUuid1,
    hasVf2: !!vfUuid2,
    hasContrast: !!contrastUuid,
    hasColorVision: !!colorVisionUuid,
    loading,
  };
}
